import React, { useState, useCallback } from "react";
import { CategoryType, CreateProductType } from "../../types";
import { useGetCategoryQuery } from "../../../../../hooks/CategoryManagement/useGetCategoryQuery";

type DropdownProps = {
  label?: string;
  placeholder: string;
  payload: CreateProductType;
  setPayload: React.Dispatch<React.SetStateAction<CreateProductType>>;
};

const Dropdown: React.FC<DropdownProps> = ({
  label,
  placeholder,
  setPayload,
  payload,
}) => {
  const [active, setActive] = useState(false);
  const { data } = useGetCategoryQuery({});

  const toggleDropdown = useCallback(() => {
    setActive((prevActive) => !prevActive);
  }, []);

  const handleCategoryChange = (categoryId: string) => {
    setPayload((prevPayload) => {
      const isSelected = prevPayload.categories.includes(categoryId);
      const newCategories = isSelected
        ? prevPayload.categories.filter((id) => id !== categoryId)
        : [...prevPayload.categories, categoryId];

      return { ...prevPayload, categories: newCategories };
    });
  };

  const selectedCategories = data?.data?.docs.filter((category: CategoryType) =>
    payload.categories.includes(category.id)
  );

  return (
    <>
      {!!label && (
        <label
          htmlFor={label}
          className="block mb-2 text-grey text-[16px] manrope capitalize"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <div
          className={`p-3 mb-3 text-[16px] bg-white w-full border-[1px] duration-300 cursor-pointer border-[#d3d3d3] focus:border-orange rounded-md
           ${active ? "border-orange" : "border-[#d3d3d3]"}`}
          onClick={toggleDropdown}
        >
          <span className="flex justify-between">
            <span className="text-syd truncate">{placeholder}</span>
            {active ? <UpChevron /> : <DownChevron />}
          </span>
        </div>

        {active && (
          <div className="z-10 w-full absolute top-[60px] h-[200px] overflow-y-scroll border-[1px] border-orange bg-white rounded-[10px] p-[20px] ">
            {data?.data?.docs &&
              data.data.docs.map((category: CategoryType) => (
                <div key={category.id} className="flex items-center">
                  <input
                    type="checkbox"
                    id={category.id}
                    checked={payload.categories.includes(category.id)}
                    onChange={() => handleCategoryChange(category.id)}
                    className="mr-2"
                  />
                  <label htmlFor={category.id}>{category.name}</label>
                </div>
              ))}
          </div>
        )}
      </div>
      {selectedCategories?.length > 0 && (
        <div>
          <div className="bg-grey text-center rounded-full p-[4px] text-white text-[12px]">
            Selected Options
          </div>
          <div className="relative mt-3 flex gap-2 flex-nowrap overflow-x-scroll border-[1px] border-[#d3d3d3] rounded-md p-2">
            {selectedCategories?.map((category: CategoryType) => (
              <button
                key={category.id}
                className="text-[14px] p-2 bg-primary-light rounded-md min-w-[160px]"
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default Dropdown;

const UpChevron = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="size-4 mt-1"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m4.5 15.75 7.5-7.5 7.5 7.5"
      />
    </svg>
  );
};

const DownChevron = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="size-4 mt-1"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m19.5 8.25-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
};
